/**
 * GlobalCursor — replaces the native cursor site-wide with a small dot that grows
 * into a ring over anything clickable. Rendered once at the app root.
 *
 * Reworked after the client's report of 2026-09-14 and QA 278777, which between
 * them named five faults: the cursor was invisible on grey surfaces, invisible
 * over the cookie banner, lagging behind the pointer, unchanged over clickable
 * elements, and large enough to cover the button underneath it. Each has a cause,
 * and each cause is gone:
 *
 *   - **Invisible on grey.** It used to be a white dot in `mix-blend-difference`,
 *     which on a mid-grey background inverts to almost the same grey. It is now a
 *     white dot with a dark ring and a soft shadow: one of the two is always
 *     against its opposite, on any background, with no blending to reason about.
 *   - **Invisible over the cookie banner.** Already solved by DIT-517 and guarded by
 *     `GlobalCursor.test.tsx`: the banner stacks above this dot, so `index.css`
 *     hands the native cursor back inside `#cc-main` and `HIDE_OVER` hides the dot
 *     there. Left exactly as it is.
 *   - **Lagging.** Every `mousemove` used to walk up to eight ancestors calling
 *     `getComputedStyle` (to decide whether the backdrop was blue) and then write
 *     a transform. The blue variant is gone with the blending, and the write is
 *     now once per animation frame, which is as often as it can be shown.
 *   - **Covering the button.** The ring is 28px rather than 36px.
 *
 * It is a pointer-only flourish: a touch screen, a coarse pointer, a visitor who
 * asked for reduced motion or for forced colours keeps the cursor their system
 * gives them. Client-only; SSG renders nothing.
 */
import { useEffect, useRef, useState } from "react";

export const HIDE_OVER = "[data-presence-zone], input, textarea, select, [contenteditable], #cc-main";
const CLICKABLE = 'a, button, [role="button"], label, select, summary, [data-clickable]';

/** Whether this visitor should get the custom cursor at all. */
export function wanted(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  const no = ["(pointer: coarse)", "(prefers-reduced-motion: reduce)", "(forced-colors: active)"];
  return !no.some((q) => window.matchMedia(q).matches);
}

export function GlobalCursor() {
  const wrap = useRef<HTMLDivElement>(null);
  const [shown, setShown] = useState(false);
  const [hot, setHot] = useState(false);
  const shownRef = useRef(false);
  const hotRef = useRef(false);

  useEffect(() => {
    if (!wanted()) return;
    document.body.classList.add("global-cursor");

    // One write per frame. A trackpad reports far more moves than the screen can
    // show, and the extra writes were the lag they produced.
    let pending: { x: number; y: number } | null = null;
    let frame = 0;
    const flush = () => {
      frame = 0;
      const w = wrap.current;
      if (w && pending) w.style.transform = `translate(${pending.x}px, ${pending.y}px)`;
    };

    const onMove = (e: MouseEvent) => {
      // Narrow, don't cast: a synthetic mousemove on `window` can carry a target
      // that is not an Element — `document` — which has no `closest`.
      const t = e.target instanceof Element ? e.target : null;
      const hide = !!t?.closest(HIDE_OVER);
      const clickable = !hide && !!t?.closest(CLICKABLE);
      pending = { x: e.clientX, y: e.clientY };
      if (!frame) frame = requestAnimationFrame(flush);
      if (shownRef.current === hide) { shownRef.current = !hide; setShown(!hide); }
      if (hotRef.current !== clickable) { hotRef.current = clickable; setHot(clickable); }
    };
    const onLeave = () => { shownRef.current = false; setShown(false); };

    window.addEventListener("mousemove", onMove, { passive: true });
    document.addEventListener("mouseleave", onLeave);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      document.body.classList.remove("global-cursor");
      window.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseleave", onLeave);
    };
  }, []);

  return (
    <div
      ref={wrap}
      aria-hidden
      className={`pointer-events-none fixed left-0 top-0 z-[9999] transition-opacity duration-150 will-change-transform ${shown ? "opacity-100" : "opacity-0"}`}
    >
      {/* one pulsing wave — only over a clickable; emanates from the ring edge */}
      <span className={`absolute left-0 top-0 h-7 w-7 rounded-full border border-white/70 ${hot ? "ds-cursor-ripple" : "hidden"}`} />
      {/* hollow ring — appears over a clickable */}
      <span className={`absolute left-0 top-0 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow-[0_0_0_1px_rgba(15,23,42,0.55)] transition-all duration-200 ease-out ${hot ? "h-7 w-7 opacity-100" : "h-0 w-0 opacity-0"}`} />
      {/* centre dot — always. White against a dark hairline, so it reads on any surface. */}
      <span className={`absolute left-0 top-0 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white shadow-[0_0_0_1px_rgba(15,23,42,0.55),0_1px_3px_rgba(15,23,42,0.45)] transition-all duration-200 ease-out ${hot ? "h-1.5 w-1.5" : "h-2.5 w-2.5"}`} />
    </div>
  );
}
